import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarPlus, CheckCircle, FileText, Image as ImageIcon } from "@phosphor-icons/react";
import { PortalShell } from "@/components/PortalShell";
import { PortalMessages } from "@/components/PortalMessages";
import { portalApi, portalAsset, portalDate } from "@/lib/portalApi";
import { fmtTime12 } from "@/lib/scheduleWindows";

const STAGES = ["Quote", "Template", "Fabrication", "Install", "Complete"];

const DateRow = ({ label, date, time, window }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-slate-200 py-2.5 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-semibold text-slate-900">
      {date
        ? `${portalDate(date)}${time ? ` · ${fmtTime12(time)}` : ""}${window && window !== "All day" ? ` · ${window}` : ""}`
        : "To book"}
    </span>
  </div>
);

export default function PortalJob() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [justConfirmed, setJustConfirmed] = useState(false);

  useEffect(() => {
    portalApi
      .get(`/jobs/${id}`)
      .then(({ data }) => setJob(data))
      .catch(() => {});
  }, [id]);

  if (!job)
    return (
      <PortalShell back="/portal/home">
        <div className="mt-10 h-10 w-10 animate-pulse rounded-full bg-slate-100" />
      </PortalShell>
    );

  const reached = Math.max(
    STAGES.findIndex(
      (s) => s.toLowerCase() === (job.stage || "").toLowerCase(),
    ),
    0,
  );

  const addToCalendar = async () => {
    try {
      const { data } = await portalApi.get(`/jobs/${job.id}/appointment.ics`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `appointment-${job.code}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* nothing booked yet */
    }
  };

  const confirmAppt = async () => {
    try {
      await portalApi.post(`/jobs/${job.id}/confirm`);
      setJustConfirmed(true);
    } catch {
      /* ignore */
    }
  };

  return (
    <PortalShell
      back="/portal/home"
      title={job.project_name}
      subtitle={`${job.code} · ${job.address || ""}`}
    >
      <div
        className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px]"
        data-testid="portal-job-page"
      >
        <div className="min-w-0 space-y-6">
          <section
            className="rounded-2xl border border-slate-200 bg-white p-5"
            data-testid="portal-job-progress"
          >
            <div className="flex flex-wrap items-center gap-2">
              {STAGES.map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <span
                    data-testid={`portal-stage-${s.toLowerCase()}`}
                    className={`rounded-full px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-widest ${
                      i <= reached
                        ? "bg-[#c39e6d] text-white"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {s}
                  </span>
                  {i < STAGES.length - 1 && (
                    <span className="h-px w-5 bg-slate-100" />
                  )}
                </div>
              ))}
            </div>
            {job.summary && (
              <p className="mt-4 text-sm text-slate-500">{job.summary}</p>
            )}
            {job.visibility?.show_job_dates !== false && (
              <div className="mt-4">
                <DateRow
                  label="Template"
                  date={job.template_date}
                  time={job.template_time}
                  window={job.template_window}
                />
                <DateRow
                  label="Fabrication"
                  date={job.fab_date}
                  time={job.fab_time}
                  window={job.fab_window}
                />
                <DateRow
                  label="Install"
                  date={job.install_date}
                  time={job.install_time}
                  window={job.install_window}
                />
                {(job.template_date || job.fab_date || job.install_date) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-testid="portal-add-to-calendar"
                      onClick={addToCalendar}
                      className="inline-flex items-center gap-2 rounded-full border border-[#c39e6d] px-4 py-2 text-xs font-semibold text-[#c39e6d] transition-colors duration-150 hover:bg-[#c39e6d] hover:text-white"
                    >
                      <CalendarPlus size={15} weight="bold" /> Add to calendar
                    </button>
                    {job.appt_confirmed_at || justConfirmed ? (
                      <span
                        data-testid="portal-confirmed"
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-500 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-700"
                      >
                        <CheckCircle size={15} weight="fill" /> Appointment confirmed
                      </span>
                    ) : (
                      <button
                        type="button"
                        data-testid="portal-confirm-appointment"
                        onClick={confirmAppt}
                        className="inline-flex items-center gap-2 rounded-full bg-[#c39e6d] px-4 py-2 text-xs font-semibold text-white transition-colors duration-150 hover:bg-[#8a6733]"
                      >
                        <CheckCircle size={15} weight="bold" /> Confirm appointment
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>

          {(job.photos || []).length > 0 && (
            <section
              className="rounded-2xl border border-slate-200 bg-white p-5"
              data-testid="portal-job-photos"
            >
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg">
                <ImageIcon size={18} weight="bold" className="text-slate-500" />{" "}
                Photos
              </h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {job.photos.map((p) => (
                  <figure key={p.id} data-testid={`portal-photo-${p.id}`}>
                    <img
                      src={portalAsset(`/jobs/${job.id}/photos/${p.id}`)}
                      alt={p.caption || "Job photo"}
                      className="h-44 w-full rounded-xl object-cover"
                    />
                    {p.caption && (
                      <figcaption className="mt-1.5 text-xs text-slate-500">
                        {p.caption}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </section>
          )}

          {(job.documents || []).length > 0 && (
            <section
              className="rounded-2xl border border-slate-200 bg-white p-5"
              data-testid="portal-job-documents"
            >
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg">
                <FileText size={18} weight="bold" className="text-slate-500" />{" "}
                Documents
              </h2>
              <div className="mt-3 divide-y divide-slate-200">
                {job.documents.map((d) => (
                  <a
                    key={d.id}
                    href={portalAsset(`/jobs/${job.id}/files/${d.id}`)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`portal-document-${d.id}`}
                    className="flex items-center justify-between gap-3 py-3 text-sm text-slate-700 hover:text-slate-900"
                  >
                    <span className="truncate">{d.name}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      Open
                    </span>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        {job.visibility?.allow_notes !== false && (
          <aside
            className="lg:sticky lg:top-6"
            data-testid="portal-notes-panel"
          >
            <PortalMessages subjectType="job" subjectId={job.id} />
          </aside>
        )}
      </div>
    </PortalShell>
  );
}
